import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ITreeOptions, TreeComponent, TreeNode } from '@odymaui/angular-tree-component';
import { NavTree, User as UserInt, Workspace } from '@local/client-contracts';
import { isEmbed, isMac, isNativeWindow } from '@local/common-web';
import { PopupRef, PopupService, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData } from '@shared/components';
import { EventsService, LogService } from '@shared/services';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { getRouteById } from '@shared/utils/routes.util';
import { Logger } from '@unleash-tech/js-logger';
import { MenuItem } from 'primeng/api';
import { TieredMenu } from 'primeng/tieredmenu';
import { Observable, take } from 'rxjs';
import { MainRoute, routes } from 'src/app/bar/routes/main-routes-config';
import { FyiService } from 'src/app/bar/services';
import { HelpPopupService } from 'src/app/bar/services/help-popup.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { NavTreeService } from 'src/app/bar/services/nav-tree.service';
import { SidebarService } from 'src/app/bar/services/sidebar.service';
import { EmbedService } from '@shared/embed.service';
import { AvatarPopupComponent, AvatarPopupData } from '../avatar-popup/avatar-popup.component';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { NgScrollbar } from 'ngx-scrollbar';

@UntilDestroy()
@Component({
  selector: 'minimized-sidebar',
  templateUrl: './minimized-sidebar.component.html',
  styleUrls: ['./minimized-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MinimizedSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(TreeComponent) private tree: TreeComponent;
  @ViewChild(TieredMenu) menu: TieredMenu;
  @ViewChild('helpButton', { read: ElementRef }) helpButtonRef: ElementRef;
  @ViewChild('fyiButton', { read: ElementRef }) fyiButtonRef: ElementRef;
  @ViewChild('avatarElement') avatarElement: ElementRef;

  @Input() workspace: Workspace.Workspace;
  @Input() workspaceIcon: UiIconModel;
  @Input() user: UserInt.Info;
  @Input() isLauncher: boolean;
  @Input() onTrialPlan: boolean;
  @Input() planDaysLeft: number;

  @Output() emitLauncherAvatarPopup = new EventEmitter();
  @Output() helpClick: EventEmitter<ElementRef> = new EventEmitter();
  @Output() fyiClick: EventEmitter<ElementRef> = new EventEmitter();
  @Output() trialBannerClick = new EventEmitter<void>();

  private readonly isMac = isMac();
  private readonly isNativeWindow = isNativeWindow();
  private avatarPopupRef: PopupRef<AvatarPopupComponent, AvatarPopupData>;
  private logger: Logger;
  private scrollbar = viewChild<NgScrollbar>('scrollbarRef');

  menuItems: MenuItem[];
  treeOptions: ITreeOptions;
  activeNode: any;
  activeParents = [];
  nodes: NavTree.Node[] = [];
  bottomMenuItems: MainRoute[] = [];
  BOTTOM_MENU_ITEMS_IDS = ['admin'];
  isUserBadgeOpen: boolean;
  hoveringNode: TreeNode;
  isEmbed: boolean = isEmbed();
  helpMenuRef: PopupRef<ContextMenuComponent, ContextMenuData>;
  unreadFyi$: Observable<number>;
  shortenDraggableArea: boolean;
  embedInline: boolean;
  commandQuickActionsText = `Quick Actions ${this.isMac ? '⌘ K' : 'CTRL K'}`;
  hasScroll: WritableSignal<boolean> = signal(false);

  get isFyiActive$() {
    return this.fyiService.active$;
  }
  get isHelpActive$() {
    return this.helpPopupService.active$;
  }

  constructor(
    private navTreeService: NavTreeService,
    private sidebarService: SidebarService,
    private cdr: ChangeDetectorRef,
    private hubService: HubService,
    private eventsService: EventsService,
    private popupService: PopupService,
    private sessionService: SessionService,
    private logService: LogService,
    private fyiService: FyiService,
    private helpPopupService: HelpPopupService,
    private embedService: EmbedService,
    private routerService: RouterService,
    private keyboardService: KeyboardService,
    private searchPopUpService: SearchPopUpService
  ) {
    this.logger = logService.scope(this.constructor.name);
  }

  public ngOnInit(): void {
    this.sidebarService.activeNode$.pipe(untilDestroyed(this)).subscribe((activeNode) => {
      this.activeNode = activeNode;
    });
    this.sidebarService.activeParents$.pipe(untilDestroyed(this)).subscribe((activeParents) => {
      this.activeParents = activeParents;
    });
    this.bottomMenuItems = this.BOTTOM_MENU_ITEMS_IDS.map((id) => getRouteById(id, routes));
    this.unreadFyi$ = this.fyiService.unread$;

    this.routerService.active$.pipe(untilDestroyed(this)).subscribe((page) => {
      this.shortenDraggableArea = !this.isMac && this.isNativeWindow && page === 'home';
      this.cdr.markForCheck();
    });
    this.setEmbedInline();
  }

  public ngAfterViewInit(): void {
    this.treeOptions = this.sidebarService.getTreeOptions(this.tree);

    this.sidebarService.nodes$.pipe(untilDestroyed(this)).subscribe((nodes) => {
      this.nodes = nodes;
      this.cdr.markForCheck();
    });

    setTimeout(() => {
      if (this.tree) this.tree.treeModel.update();
      this.sidebarService.loadTree(this.tree, this.activeNode);
    }, 50);
  }

  public ngOnDestroy(): void {
    this.avatarPopupRef?.destroy();
    if (this.helpMenuRef) {
      this.helpMenuRef.destroy();
      this.helpMenuRef = null;
    }
    this.sidebarService.activeNode$.next(this.activeNode);
    this.navTreeService.unsubscribeAll();
  }

  async setEmbedInline() {
    if (this.isEmbed) {
      this.embedInline = await this.embedService?.isInline();
    }
  }

  createNodeMenuTree(node): MenuItem {
    const label = node.data?.location ?? node.id;

    if (!node) return;
    const menuObject: MenuItem = {
      label: node.title,
      command: () => {
        const jsonData = JSON.stringify({ window: { side_panel_mode: 'close' } });
        this.eventsService.event('tree.click', { label, location: { title: this.hubService.currentLocation }, jsonData });
        this.routerService.navigateByUrl(node.id);
      },
      id: node.id,
      styleClass: node.id === this.activeNode?.id || this.isParentActive(node) ? 'active-item' : '',
    };
    if (!node.hasChildren) return menuObject;
    menuObject.items = [];
    node.children.forEach((child) => {
      menuObject.items.push(this.createNodeMenuTree(child));
    });
    return menuObject;
  }

  isParentActive(node): boolean {
    if (!this.activeParents) return false;
    return this.activeParents.find((parent) => {
      return parent.id === node.id;
    });
  }

  unsubscribe(id: string) {
    this.navTreeService.unsubscribe(id);
  }

  async signOut(): Promise<void> {
    await this.sessionService.signOut();
  }
  onQuickActionsClick(event) {
    this.searchPopUpService.open('mouse_click', 'MinimizedSidebar');
  }
  onFyiClick() {
    this.fyiClick.emit(this.fyiButtonRef);
  }

  onHelpClick(event: MouseEvent) {
    event.stopPropagation();
    this.helpClick.emit(this.helpButtonRef);
  }

  async onAvatarClick(event: MouseEvent): Promise<void> {
    if (this.isLauncher || (this.isEmbed && !this.embedInline)) {
      this.emitLauncherAvatarPopup.emit();
      return;
    }

    this.isUserBadgeOpen = true;
    const { x, y } = this.avatarElement.nativeElement.getBoundingClientRect();
    this.eventsService.event('hub_side_menu.profile_button_open', {
      location: { title: this.hubService.currentLocation },
    });

    const offset: Partial<ConnectedPosition> = { offsetY: 36, offsetX: 2 };
    const position: ConnectedPosition[] = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top', ...offset }];

    const location: string = this.hubService.currentLocation;

    this.avatarPopupRef = this.popupService.open<AvatarPopupComponent, AvatarPopupData>(
      { x, y },
      AvatarPopupComponent,
      {
        email: this.user.email,
        location: { title: location },
        workspace: this.workspace,
        workspaceIcon: this.workspaceIcon,
        hideLogout: this.embedInline,
      },
      { position }
    );

    this.avatarPopupRef.compInstance.logout.pipe(take(1)).subscribe(() => this.signOut());
    this.avatarPopupRef.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isUserBadgeOpen = false;
      this.eventsService.event('hub_side_menu.profile_button_close', {
        location: { title: location },
      });
    });
  }

  updateScrollBar() {
    this.hasScroll.set(this.scrollbar()?.state.verticalUsed);
  }
}
